import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { H2, XStack } from 'tamagui';
import { Toggle } from 'tamagui-config';

import { ColorPickerCard } from './components/ColorPickerCard';
import { MenuItem } from './components/MenuItem';
import { PatternSelector } from './components/PatternSelector';
import { BacklightPattern, useBacklight } from '../../hooks/firmware/useBacklight';
import { StyledCard } from '../Settings_General/components/styles';

enum BacklightMenuItem {
  ENABLE_BACKLIGHT = 'enableBacklight',
  PATTERN = 'pattern',
}

export const BacklightSettings: React.FC = () => {
  const { t } = useTranslation();

  const [isToggling, setIsToggling] = React.useState(false);
  const [isPatternChanging, setIsPatternChanging] = React.useState(false);
  const [currentPattern, setCurrentPattern] = React.useState<BacklightPattern>('Solid');
  const {
    turnOnBacklight,
    turnOffBacklight,
    backlightStatus,
    backlightPattern,
    setBacklightPattern,
  } = useBacklight();


  const [isBacklightEnabled, setIsBacklightEnabled] = React.useState<boolean>(
    backlightStatus.data?.status.current_effect !== 'Off',
  );

  const onTogglePin = async (checked: boolean) => {
    // Prevent changes when alerts are active

    // Store the previous state for fallback
    const previousState = isBacklightEnabled;

    // Optimistically update the UI
    setIsBacklightEnabled(checked);
    setIsToggling(true);

    try {
      if (checked) {
        await turnOnBacklight();
      } else {
        await turnOffBacklight();
      }
    } catch (error) {
      console.error('Failed to toggle backlight:', error);
      setIsBacklightEnabled(previousState);
      // Optionally show a toast or error message here
    } finally {
      setIsToggling(false);
    }
  };

  const handlePatternChange = async (pattern: BacklightPattern) => {
    // Prevent changes when alerts are active

    setIsPatternChanging(true);
    setCurrentPattern(pattern);

    try {
      await setBacklightPattern(pattern);
    } catch (error) {
      console.error('Error changing backlight pattern:', error);
    } finally {
      setIsPatternChanging(false);
    }
  };

  const renderMenuItem = (itemType: BacklightMenuItem) => {
    switch (itemType) {
      case BacklightMenuItem.ENABLE_BACKLIGHT:
        return (
          <MenuItem
            rightElement={
              <Toggle
                size="$4"
                checked={isBacklightEnabled}
                onCheckedChange={onTogglePin}
                disabled={isToggling}
              />
            }
            label={
              isBacklightEnabled
                ? t('settings.backlight.backlightEnabled')
                : t('settings.backlight.backlightDisabled')
            }
          />
        );
      case BacklightMenuItem.PATTERN:
        return (
          <MenuItem
            rightElement={
              <PatternSelector
                value={currentPattern}
                onChange={handlePatternChange}
                isDisabled={!isBacklightEnabled || isPatternChanging}
              />
            }
            label={t('settings.backlight.pattern')}
          />
        );
    }
  };

  // Initialize backlight settings on component mount
  useEffect(() => {
    const initializeSettings = async () => {
      try {
        await backlightStatus.execute();

        // Let alert-based backlight system handle all pattern/color management
        // No manual pattern initialization to avoid conflicts
      } catch (error) {
        console.error('Error initializing backlight settings:', error);
      }
    };

    initializeSettings();
  }, []);

  // Update local state when backlight status changes
  useEffect(() => {
    if (backlightStatus.data) {
      // Use the current_effect property - 'Off' means disabled, any other effect means enabled
      setIsBacklightEnabled(backlightStatus.data.status.current_effect !== 'Off');
    }
  }, [backlightStatus.data]);

  // Update local state when backlight pattern changes
  useEffect(() => {
    if (backlightPattern.data) {
      // Show firmware pattern when no alerts
      setCurrentPattern(backlightPattern.data.pattern);
    }
  }, [backlightPattern.data]);

  return (
    <XStack padding="$5" gap="$5">
      <StyledCard height="432px" flex={1}>
        <H2 color="$white12" fontSize="$6" lineHeight="$5">
          {t('settings.backlight.title')}
        </H2>
        {renderMenuItem(BacklightMenuItem.ENABLE_BACKLIGHT)}
        {renderMenuItem(BacklightMenuItem.PATTERN)}
      </StyledCard>
      <ColorPickerCard isEnabled={isBacklightEnabled} />
    </XStack>
  );
};
